import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import useUsers from './useUsers';
import { useFileUpload } from 'hooks/useFileUpload';

const extractAddressData = (address, prefix) => {
  if (!address) return {};
  if (typeof address === 'string') {
    return { [`${prefix}Street`]: address };
  }
  return {
    [`${prefix}Street`]: address.street || '',
    [`${prefix}City`]: address.city || '',
    [`${prefix}State`]: address.state || '',
    [`${prefix}ZipCode`]: address.zipCode || '',
    [`${prefix}Country`]: address.country || ''
  };
};

const extractContactData = (c) => {
  if (!c) return {};
  return {
    email: c.email || c.personalEmail || '',
    phone: c.mobilePhone || c.phone || '',
    mobilePhone: c.mobilePhone || '',
    homePhone: c.homePhone || '',
    workPhone: c.workPhone || '',
    personalEmail: c.personalEmail || '',
    workEmail: c.email || '',
    website: c.website || '',
    twitter: c.twitter || '',
    facebook: c.facebook || '',
    linkedin: c.linkedin || ''
  };
};

const shallowDiff = (newObj, oldObj = {}) => {
  const diff = {};
  Object.keys(newObj).forEach(key => {
    const newVal = newObj[key] ?? '';
    const oldVal = oldObj[key] ?? '';
    if (newVal !== oldVal) diff[key] = newVal;
  });
  return diff;
};

const identityFields = [
  'username', 'email', 'phone',
  'firstName', 'middleName', 'lastName', 'status'
];

export const useAddUserForm = (
  user = null,
  { onSuccess, initialStaff = null } = {},
  externalFormData = null,
  setExternalFormData = null
) => {
  const isEditMode = !!user;
  const lastUserIdRef = useRef(null);

  const {
    uploadFile,
    uploading: uploadingPhoto,
    error: uploadError,
    submitIdentityVerification,
    submitWorkAuthorization
  } = useFileUpload();
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);

  // Extended initialFormData with complete verification objects
  const initialFormData = {
    firstName: '', middleName: '', lastName: '', username: '', email: '', password: '',
    phone: '', title: '', profession: '', birthDate: '', gender: '', ssn: '', taxIdType: '', taxId: '', status: 'ACTIVE',
    preferredMailingAddress: '', preferredContactMethod: '', nextOfKin: '',
    identificationDocumentType: '',
    mobilePhone: '', homePhone: '', workPhone: '', personalEmail: '', workEmail: '',
    website: '', twitter: '', facebook: '', linkedin: '',
    profilePhoto: '',
    permanentStreet: '', permanentCity: '', permanentState: '', permanentZipCode: '', permanentCountry: '',
    residentialStreet: '', residentialCity: '', residentialState: '', residentialZipCode: '', residentialCountry: '',
    workStreet: '', workCity: '', workState: '', workZipCode: '', workCountry: '',
    schoolStreet: '', schoolCity: '', schoolState: '', schoolZipCode: '', schoolCountry: '',
    billingStreet: '', billingCity: '', billingState: '', billingZipCode: '', billingCountry: '',
    // Complete verification objects matching API structure
    userIdentityVerification: {
      idDocumentIds: [],
      identificationDocumentType: '',
      verified: false,
      verifiedOn: null,
      uploadedOn: null,
      uploadedBy: null,
      userProfileId: null
    },
    userWorkAuthorization: {
      idDocumentIds: [],
      capability: '',
      type: '',
      workAuthorizationType: '',
      canWork: null,
      requiresVerification: null,
      hasRestrictions: null,
      expirationDate: null,
      notes: '',
      verified: false,
      verifiedOn: null,
      uploadedOn: null,
      uploadedBy: null,
      userProfileId: null
    }
  };

  const [internalFormData, setInternalFormData] = useState(initialFormData);
  const formData = externalFormData ?? internalFormData;
  const setFormData = setExternalFormData ?? setInternalFormData;

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    createUser,
    patchUserProperties,
    patchProfileProperties,
    loading: userLoading,
    error: userError,
    refetch
  } = useUsers(0, 20, initialStaff);

  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const errorsRef = useRef(errors);
  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const isEditModeRef = useRef(isEditMode);
  useEffect(() => {
    isEditModeRef.current = isEditMode;
  }, [isEditMode]);

  useEffect(() => {
    if (!user) {
      lastUserIdRef.current = null;
      setFormData(initialFormData);
      return;
    }
    if (lastUserIdRef.current === user.id) return;

    const addressData = {
      ...extractAddressData(user.permanentAddress, 'permanent'),
      ...extractAddressData(user.residentialAddress, 'residential'),
      ...extractAddressData(user.workAddress, 'work'),
      ...extractAddressData(user.schoolAddress, 'school'),
      ...extractAddressData(user.billingAddress, 'billing')
    };

    const contactData = extractContactData(user.contactDetails);

    setFormData({
      ...initialFormData,
      ...addressData,
      ...contactData,
      firstName: user.firstName || '',
      middleName: user.middleName || '',
      lastName: user.lastName || '',
      username: user.username || '',
      email: contactData.email || user.email || '',
      phone: contactData.phone || user.phone || '',
      mobilePhone: contactData. mobilePhone || user. mobilePhone || '',
      workPhone: contactData. workPhone || user. workPhone || '', 
      homePhone: contactData. homePhone || user. homePhone || '',
      personalEmail: contactData. personalEmail || user. personalEmail || '',
      workEmail: contactData. workEmail || user. workEmail || '',
      website: contactData. website || user. website || '',
      twitter: contactData. twitter || user. twitter || '',
      facebook: contactData. facebook || user. facebook || '',
      linkedin: contactData. linkedin || user. linkedin || '',
      profilePhoto: user.profilePhoto || '',
      birthDate: user.birthDate ? user.birthDate.split('T')[0] : '',
      gender: user.gender || '',
      ssn: user.ssn || '',
      taxIdType: user.taxIdType || '',
      taxId: user.taxId || '',

      status: user.status || 'ACTIVE',
      preferredMailingAddress: user.preferredMailingAddress || '',
      preferredContactMethod: user.preferredContactMethod || '',
      nextOfKin: user.nextOfKin || '',
      identificationDocumentType: user.identificationDocumentType || '',
      title: user.title || '',
      profession: user.profession || '',
      userIdentityVerification: {
        idDocumentIds: user.userIdentityVerification?.idDocumentIds || [],
        identificationDocumentType: user.userIdentityVerification?.identificationDocumentType || user.identificationDocumentType || '',
        verified: user.userIdentityVerification?.verified || false,
        verifiedOn: user.userIdentityVerification?.verifiedOn || null,
        uploadedOn: user.userIdentityVerification?.uploadedOn || null,
        uploadedBy: user.userIdentityVerification?.uploadedBy || null,
        userProfileId: user.userIdentityVerification?.userProfileId || user.id || null
      },
      userWorkAuthorization: {
        idDocumentIds: user.userWorkAuthorization?.idDocumentIds || [],
        capability: user.userWorkAuthorization?.capability || '',
        type: user.userWorkAuthorization?.type || user.userWorkAuthorization?.workAuthorizationType || '',
        workAuthorizationType: user.userWorkAuthorization?.workAuthorizationType || user.userWorkAuthorization?.type || '',
        canWork:
          typeof user.userWorkAuthorization?.canWork === 'boolean'
            ? user.userWorkAuthorization.canWork
            : null,
        requiresVerification:
          typeof user.userWorkAuthorization?.requiresVerification === 'boolean'
            ? user.userWorkAuthorization.requiresVerification
            : null,
        hasRestrictions:
          typeof user.userWorkAuthorization?.hasRestrictions === 'boolean'
            ? user.userWorkAuthorization.hasRestrictions
            : null,
        expirationDate: user.userWorkAuthorization?.expirationDate || null,
        notes: user.userWorkAuthorization?.notes || '',
        verified: user.userWorkAuthorization?.verified || false,
        verifiedOn: user.userWorkAuthorization?.verifiedOn || null,
        uploadedOn: user.userWorkAuthorization?.uploadedOn || null,
        uploadedBy: user.userWorkAuthorization?.uploadedBy || null,
        userProfileId: user.userWorkAuthorization?.userProfileId || user.id || null
      }
    });

    lastUserIdRef.current = user.id;
  }, [user, setFormData]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => {
      if (prev[name]) {
        const { [name]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, [setFormData]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    setProfilePhotoFile(file);
  }, []);

  // New handler for Identification Document file input
  const handleIdentificationDocChange = useCallback((input) => {
    const files = Array.isArray(input)
      ? input
      : Array.from(input?.target?.files || []);
    setFormData(prev => ({
      ...prev,
      userIdentityVerification: {
        ...prev.userIdentityVerification,
        idDocumentIds: files,
        uploadedOn: new Date().toISOString()
      }
    }));
  }, [setFormData]);

  // New handler for Work Authorization Document file input
  const handleWorkAuthorizationDocChange = useCallback((input) => {
    const files = Array.isArray(input)
      ? input
      : Array.from(input?.target?.files || []);
    setFormData(prev => ({
      ...prev,
      userWorkAuthorization: {
        ...prev.userWorkAuthorization,
        idDocumentIds: files,
        uploadedOn: new Date().toISOString()
      }
    }));
  }, [setFormData]);

  const handleWorkAuthorizationChange = useCallback((updates = {}) => {
    setFormData(prev => ({
      ...prev,
      userWorkAuthorization: {
        ...prev.userWorkAuthorization,
        ...updates
      }
    }));
  }, [setFormData]);

  // Handler for identification document type change
  const handleIdentificationDocTypeChange = useCallback((input) => {
    const value = typeof input === 'string' ? input : input?.target?.value;
    setFormData(prev => ({
      ...prev,
      identificationDocumentType: value,
      userIdentityVerification: {
        ...prev.userIdentityVerification,
        identificationDocumentType: value
      }
    }));
  }, [setFormData]);

  // Handler for work authorization type change
  const handleWorkAuthorizationTypeChange = useCallback((input) => {
    const value = typeof input === 'string' ? input : input?.target?.value;
    handleWorkAuthorizationChange({
      workAuthorizationType: value,
      type: value
    });
  }, [handleWorkAuthorizationChange]);

  const validateForm = useCallback(() => {
    const formData = formDataRef.current;
    const required = ['firstName', 'lastName', 'username', 'email', 'phone'];
    if (!isEditModeRef.current) required.push('password');

    const newErrors = {};
    required.forEach(key => {
      if (!formData[key]?.trim()) {
        newErrors[key] = `${key} is required`;
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, newErrors };
  }, []);

  const prepareIdentityPayload = useCallback(() => {
    const formData = formDataRef.current;
    const payload = {
      email: formData.email?.trim(),
      password: formData.password,
      username: formData.username?.trim(),
      phone: formData.phone?.trim(),
      firstName: formData.firstName?.trim(),
      middleName: formData.middleName?.trim() || null,
      lastName: formData.lastName?.trim(),
      status: formData.status || 'ACTIVE'
    };

    if (formData.title?.trim()) payload.title = formData.title.trim();
    if (formData.profession?.trim()) payload.profession = formData.profession.trim();

    if (formData.permanentStreet?.trim()) payload.address = formData.permanentStreet.trim();
    if (formData.permanentCity?.trim()) payload.city = formData.permanentCity.trim();
    if (formData.permanentState?.trim()) payload.state = formData.permanentState.trim();
    if (formData.permanentZipCode?.trim()) payload.zipCode = formData.permanentZipCode.trim();
    if (formData.permanentCountry?.trim()) payload.country = formData.permanentCountry.trim();

    return Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== null && v !== undefined)
    );
  }, []);

  const buildAddress = useCallback((prefix) => {
    const formData = formDataRef.current;
    return {
      street: formData[`${prefix}Street`] || '',
      city: formData[`${prefix}City`] || '',
      state: formData[`${prefix}State`] || '',
      zipCode: formData[`${prefix}ZipCode`] || '',
      country: formData[`${prefix}Country`] || ''
    };
  }, []);

  const prepareProfilePayload = useCallback(() => {
    const formData = formDataRef.current;
    const contactDetails = {
      mobilePhone: formData.mobilePhone?.trim() || formData.phone?.trim() || null,
      homePhone: formData.homePhone?.trim() || null,
      workPhone: formData.workPhone?.trim() || null,
      personalEmail: formData.personalEmail?.trim() || formData.email?.trim() || null,
      email: formData.workEmail?.trim() || formData.email?.trim() || null,
      website: formData.website?.trim() || null,
      twitter: formData.twitter?.trim() || null,
      facebook: formData.facebook?.trim() || null,
      linkedin: formData.linkedin?.trim() || null
    };

    const cleanContactDetails = Object.fromEntries(
      Object.entries(contactDetails).filter(([_, v]) => v !== null && v !== '')
    );

    const permanentAddress = buildAddress('permanent');
    const residentialAddress = buildAddress('residential');
    const workAddress = buildAddress('work');
    const schoolAddress = buildAddress('school');
    const billingAddress = buildAddress('billing');

    const payload = {
      firstName: formData.firstName?.trim(),
      middleName: formData.middleName?.trim() || null,
      lastName: formData.lastName?.trim(),
      username: formData.username?.trim(),
      profilePhoto: formData.profilePhoto?.trim() || null,
      birthDate: formData.birthDate || null,
      gender: formData.gender || null,
      ssn: formData.ssn?.trim() || null,
      taxIdType: formData.taxIdType || null,
      taxId: formData.taxId?.trim() || null,
      status: formData.status || 'ACTIVE',
      nextOfKin: formData.nextOfKin?.trim() || null,
      identificationDocumentType: formData.identificationDocumentType || null,
      contactDetails: Object.keys(cleanContactDetails).length > 0 ? cleanContactDetails : null,
      permanentAddress: permanentAddress && Object.values(permanentAddress).some(v => v) ? permanentAddress : null,
      residentialAddress: residentialAddress && Object.values(residentialAddress).some(v => v) ? residentialAddress : null,
      workAddress: workAddress && Object.values(workAddress).some(v => v) ? workAddress : null,
      schoolAddress: schoolAddress && Object.values(schoolAddress).some(v => v) ? schoolAddress : null,
      billingAddress: billingAddress && Object.values(billingAddress).some(v => v) ? billingAddress : null,
      preferredMailingAddress: formData.preferredMailingAddress || null,
      preferredContactMethod: formData.preferredContactMethod || null
    };

    return Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => {
        if (v === null) return false;
        if (typeof v === 'object' && !Array.isArray(v)) {
          return Object.values(v).some(val => val !== null && val !== '');
        }
        return true;
      })
    );
  }, [buildAddress]);

  const computeChangedProperties = useCallback(() => {
    const formData = formDataRef.current;
    const user = userRef.current;
    if (!isEditModeRef.current || !user) return { identityChanges: [], profileChanges: [] };

    const identityChanges = [];
    identityFields.forEach(key => {
      if ((formData[key] ?? '') !== (user[key] ?? '')) {
        identityChanges.push({ name: key, value: formData[key] ?? '' });
      }
    });

    const newProfile = prepareProfilePayload();

    const oldProfile = {
      birthDate: user.birthDate || '',
      gender: user.gender || '',
      ssn: user.ssn || '',
      taxIdType: user.taxIdType || '',
      taxId: user.taxId || '',
      status: user.status || '',
      nextOfKin: user.nextOfKin || '',
      identificationDocumentType: user.identificationDocumentType || '',
      preferredMailingAddress: user.preferredMailingAddress || '',
      preferredContactMethod: user.preferredContactMethod || '',
      contactDetails: user.contactDetails || {},
      permanentAddress: user.permanentAddress || {},
      residentialAddress: user.residentialAddress || {},
      workAddress: user.workAddress || {},
      schoolAddress: user.schoolAddress || {},
      billingAddress: user.billingAddress || {},
      userIdentityVerification: user.userIdentityVerification || {
        idDocumentIds: [],
        identificationDocumentType: '',
        verified: false,
        verifiedOn: null,
        uploadedOn: null,
        uploadedBy: null,
        userProfileId: null
      },
      userWorkAuthorization: {
        idDocumentIds: [],
        capability: '',
        canWork: null,
        requiresVerification: null,
        hasRestrictions: null,
        expirationDate: null,
        notes: '',
        verified: false,
        verifiedOn: null,
        uploadedOn: null,
        uploadedBy: null,
        userProfileId: null,
        ...(user.userWorkAuthorization || {}),
        type:
          user.userWorkAuthorization?.type ||
          user.userWorkAuthorization?.workAuthorizationType ||
          '',
        workAuthorizationType:
          user.userWorkAuthorization?.workAuthorizationType ||
          user.userWorkAuthorization?.type ||
          ''
      }
    };

    const profileChanges = [];

    [
      'birthDate', 'gender', 'ssn', 'taxIdType', 'taxId', 'status',
      'nextOfKin', 'identificationDocumentType',
      'preferredMailingAddress',
      'preferredContactMethod'
    ].forEach(key => {
      if ((newProfile[key] ?? '') !== (oldProfile[key] ?? '')) {
        profileChanges.push({ name: key, value: newProfile[key] });
      }
    });

    // Contact diff
    const contactDiff = shallowDiff(newProfile.contactDetails, oldProfile.contactDetails);
    if (Object.keys(contactDiff).length) {
      profileChanges.push({ name: 'contactDetails', value: contactDiff });
    }

    // Address diffs
    ['permanentAddress', 'residentialAddress', 'workAddress', 'schoolAddress', 'billingAddress']
      .forEach(addr => {
        const diff = shallowDiff(newProfile[addr], oldProfile[addr]);
        if (Object.keys(diff).length) profileChanges.push({ name: addr, value: diff });
      });

    // Document diffs: compare the complete verification objects
    const identityVerificationChanged = JSON.stringify(formData.userIdentityVerification || {}) !== JSON.stringify(oldProfile.userIdentityVerification || {});
    if (identityVerificationChanged) {
      profileChanges.push({ name: 'userIdentityVerification', value: formData.userIdentityVerification });
    }

    const workAuthorizationChanged = JSON.stringify(formData.userWorkAuthorization || {}) !== JSON.stringify(oldProfile.userWorkAuthorization || {});
    if (workAuthorizationChanged) {
      profileChanges.push({ name: 'userWorkAuthorization', value: formData.userWorkAuthorization });
    }

    return { identityChanges, profileChanges };
  }, [prepareProfilePayload]);

  const submitForm = useCallback(async () => {
    const { isValid, newErrors } = validateForm();
    if (!isValid) {
      return { success: false, errors: newErrors };
    }

    setIsSubmitting(true);

    try {
      const identityDocs = formDataRef.current.userIdentityVerification?.idDocumentIds || [];
      const workAuthDocs = formDataRef.current.userWorkAuthorization?.idDocumentIds || [];

      // Guard create flow: if docs are attached, require type + front/back
      const identityType =
        formDataRef.current.userIdentityVerification?.identificationDocumentType ||
        formDataRef.current.identificationDocumentType;
      if (identityDocs.length > 0 && (!identityType || identityDocs.length !== 2)) {
        return { success: false, errors: { identityDocs: 'Identity documents incomplete' } };
      }

      const workAuthData = formDataRef.current.userWorkAuthorization || {};
      const workAuthType =
        workAuthData.type ||
        workAuthData.workAuthorizationType;
      const workAuthCapability = workAuthData.capability;
      if (workAuthDocs.length > 0 && (!workAuthType || !workAuthCapability || workAuthDocs.length !== 2)) {
        return { success: false, errors: { workAuthDocs: 'Work authorization documents incomplete' } };
      }

      let uploadedPhotoId = null;

      if (profilePhotoFile) {
        const userId = userRef.current?.id;
        if (userId) {
          const uploadResult = await uploadFile(userId, profilePhotoFile);
          const resolved = uploadResult.file?.publicUrl || uploadResult.file?.url || uploadResult.file?.fileUrl || uploadResult.fileId;
          uploadedPhotoId = resolved;
        }
      }

      const identityPayload = prepareIdentityPayload();
      const profilePayload = prepareProfilePayload();

      if (isEditModeRef.current && userRef.current) {
        const { identityChanges, profileChanges } = computeChangedProperties();

        if (uploadedPhotoId) {
          profileChanges.push({ name: 'profilePhoto', value: uploadedPhotoId });
        }

        if (!identityChanges.length && !profileChanges.length) {
          toast.info('No changes detected.');
          return { success: true };
        }

        if (identityChanges.length) {
          await patchUserProperties(userRef.current.id, identityChanges);
        }

        if (profileChanges.length) {
          await patchProfileProperties(userRef.current.id, profileChanges);
        }

        onSuccess?.();
        await refetch();

        return { success: true };
      }

      // Create new user flow
      const { user: createdUser, profile } = await createUser(identityPayload, profilePayload);

      // If photo file selected, upload it after creation
      if (profilePhotoFile) {
        const uploadResult = await uploadFile(createdUser.id, profilePhotoFile);
        const uploadValue = uploadResult.file?.publicUrl || uploadResult.file?.url || uploadResult.file?.fileUrl || uploadResult.fileId;

        await patchProfileProperties(createdUser.id, [
          { name: 'profilePhoto', value: uploadValue }
        ]);
      }

      // Upload and patch document files
      // Upload and submit document files
      if (identityDocs.length) {
        const uploadedIds = [];
        for (const file of identityDocs) {
          const uploadResult = await uploadFile(createdUser.id, file, {
            purpose: 'USER_PROFILE',
            fileType: 'DOCUMENT'
          });
          if (uploadResult.fileId) uploadedIds.push(uploadResult.fileId);
        }
        if (uploadedIds.length) {
          const docType = identityType || 'UNKNOWN';
          await submitIdentityVerification(createdUser.id, docType, uploadedIds);
        }
      }

      if (workAuthDocs.length) {
        const uploadedIds = [];
        for (const file of workAuthDocs) {
          const uploadResult = await uploadFile(createdUser.id, file, {
            purpose: 'USER_PROFILE',
            fileType: 'DOCUMENT'
          });
          if (uploadResult.fileId) uploadedIds.push(uploadResult.fileId);
        }
        if (uploadedIds.length) {
          await submitWorkAuthorization(createdUser.id, {
            capability: workAuthCapability,
            type: workAuthType,
            workAuthorizationType: workAuthType,
            canWork: workAuthData.canWork,
            requiresVerification: workAuthData.requiresVerification,
            hasRestrictions: workAuthData.hasRestrictions,
            expirationDate: workAuthData.expirationDate || null,
            notes: workAuthData.notes || null,
            idDocumentIds: uploadedIds
          });
        }
      }

      onSuccess?.();
      await refetch();

      return { success: true };
    } catch (err) {
      return { success: false, errors: { form: err.message } };
    } finally {
      setIsSubmitting(false);
    }
  }, [
    computeChangedProperties,
    createUser,
    patchUserProperties,
    submitIdentityVerification,
    submitWorkAuthorization,
    patchProfileProperties,
    prepareIdentityPayload,
    prepareProfilePayload,
    profilePhotoFile,
    uploadFile,
    onSuccess,
    refetch
  ]);

  return {
    formData,
    errors,
    isSubmitting,
    userLoading,
    userError,
    uploadingPhoto,
    uploadError,
    setFormData,
    handleInputChange,
    handleFileChange,
    handleIdentificationDocChange,
    handleWorkAuthorizationDocChange,
    handleWorkAuthorizationChange,
    handleIdentificationDocTypeChange,
    handleWorkAuthorizationTypeChange,
    submitForm
  };
};
